// 植物アクションシート（盆栽をタップしたときに出る小さなボトムシート）。
//
// 植物デコをタップすると「のばす剪定 / ととのえる剪定 / 撤去」を選べる。
// 非植物デコは従来どおりタップで即撤去（このシートは出さない）。
// DOM 直組みの軽量実装（React なし方針）。タッチ一級・DotGothic16。

import type { GrowthStage, PruneStyle } from '../sim/plants';
import { STAGE_LABELS } from '../sim/plants';

/** シートで選べるアクション。 */
export type PlantAction = { type: 'prune'; style: PruneStyle } | { type: 'remove' };

/** シートを開くときに渡す対象植物の情報。 */
export interface PlantSheetTarget {
  gx: number;
  gy: number;
  /** 植物デコの表示名（見出しに使う）。 */
  name: string;
  /** 現在の成長段階。 */
  stage: GrowthStage;
}

export interface PlantSheet {
  /** 対象植物のシートを開く。 */
  open(target: PlantSheetTarget): void;
  close(): void;
  readonly isOpen: boolean;
}

export interface PlantSheetOptions {
  rootEl: HTMLElement; // #plantSheet
  titleEl: HTMLElement; // #plantSheetTitle
  buttonsEl: HTMLElement; // #plantSheetButtons
  /** アクション選択時のコールバック（対象の gx,gy と選んだアクション）。 */
  onAction: (gx: number, gy: number, action: PlantAction) => void;
}

export function createPlantSheet(opts: PlantSheetOptions): PlantSheet {
  const { rootEl, titleEl, buttonsEl, onAction } = opts;
  let current: PlantSheetTarget | null = null;

  function close(): void {
    rootEl.classList.remove('open');
    current = null;
  }

  function fire(action: PlantAction): void {
    if (!current) return;
    const { gx, gy } = current;
    close();
    onAction(gx, gy, action);
  }

  function makeBtn(label: string, sub: string, action: PlantAction, accent = false): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pixel-btn plant-action' + (accent ? ' accent' : '');
    const t = document.createElement('span');
    t.className = 't';
    t.textContent = label;
    const s = document.createElement('span');
    s.className = 's';
    s.textContent = sub;
    btn.append(t, s);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      fire(action);
    });
    return btn;
  }

  function open(target: PlantSheetTarget): void {
    current = target;
    titleEl.textContent = `${target.name}（${STAGE_LABELS[target.stage]}）`;
    buttonsEl.innerHTML = '';
    buttonsEl.append(
      makeBtn('のばす剪定', '枝をのばして緑ゆたかに', { type: 'prune', style: 'extend' }),
      makeBtn('ととのえる剪定', '端正に刈り込んで静けさを', { type: 'prune', style: 'tidy' }),
      makeBtn('撤去', 'チップは全額もどる', { type: 'remove' }, true),
    );
    rootEl.classList.add('open');
  }

  // 背景タップで閉じる（カードの中は stopPropagation 済み）。
  rootEl.addEventListener('click', () => close());

  return {
    open,
    close,
    get isOpen() {
      return rootEl.classList.contains('open');
    },
  };
}
